import API from './API';

// -----------------------------------------------------------------------------
//  Fornitori di mezzi e noleggi (M9 / COD8).
//
//  Due risorse distinte, che è facile confondere:
//    - `supplier`        → l'anagrafica del fornitore (chi ci noleggia i mezzi);
//    - `vehicle_rental`  → il singolo noleggio, con costo e stato di pagamento.
//
//  Sono risorse SIMBA: costi interni, nessun tenant. Il backend le riserva ai
//  ruoli globali (superadmin), quindi con un utente partner queste chiamate
//  rispondono 403 — non è un errore da nascondere, è la regola.
//
//  ⚠️ Il noleggio si crea DOPO il booking, perché ha bisogno del suo id. E
//  creandolo il backend riallinea da solo `booking.supplier_cost` alla somma
//  dei noleggi di quel booking (un Transfer può usare più mezzi): quel campo
//  non va mai scritto a mano.
// -----------------------------------------------------------------------------

function normalize(response) {
  const body = response?.data;
  if (Array.isArray(body)) return { rows: body, total: body.length };
  return { rows: body?.data ?? [], total: body?.total ?? 0 };
}

export default {
  // ----------------------------------------------------------- FORNITORI ---
  // `onlyActive` serve alle tendine: un fornitore disattivato non va più
  // proposto per nuovi noleggi, ma resta sui noleggi storici.
  getSuppliers({ onlyActive = false, search = '' } = {}) {
    const and = [];
    if (onlyActive) and.push({ is_active: { $eq: true } });
    if (search.trim()) {
      and.push({
        $or: [
          { company_name: { $contL: search.trim() } },
          { piva: { $contL: search.trim() } },
          { mail: { $contL: search.trim() } },
        ],
      });
    }
    const params = { sort: 'company_name,ASC' };
    if (and.length) params.s = JSON.stringify({ $and: and });
    return API.get('/supplier', { params }).then((r) => normalize(r).rows);
  },

  createSupplier(supplier) {
    return API.post('/supplier', supplier).then((r) => r.data);
  },

  updateSupplier(id, patch) {
    return API.patch(`/supplier/${id}`, patch).then((r) => r.data);
  },

  // Non esiste una cancellazione "vera": un fornitore con noleggi storici
  // sparirebbe dal COD8 dei mesi passati. Si disattiva.
  setSupplierActive(id, active) {
    return API.patch(`/supplier/${id}`, { is_active: !!active }).then((r) => r.data);
  },

  // ------------------------------------------------------------ NOLEGGI ---
  // Noleggi di UN booking: serve al dettaglio prenotazione.
  //
  // Si filtra sulla COLONNA `bookingId`, non su `booking.id`: il percorso
  // della relazione richiede che la relazione sia fra i join della RICHIESTA,
  // e senza risponde 500. La colonna c'è
  // sempre e non dipende dai join.
  getRentalsByBooking(bookingId) {
    if (!bookingId) return Promise.resolve([]);
    return API.get('/vehicle-rental', {
      params: {
        join: ['supplier'],
        s: JSON.stringify({ bookingId: { $eq: bookingId } }),
      },
    }).then((r) => normalize(r).rows);
  },

  // `cost` è il costo del mezzo per NOI (COD8, nessun ricavo).
  // Si scrivono le COLONNE (`bookingId`/`supplierId`), non le relazioni
  // annidate: `vehicle_rental` non ha cascade, e il backend legge da lì per
  // riallineare `booking.supplier_cost`.
  createRental({ bookingId, supplierId, vehicleType, rentalDate, cost }) {
    return API.post('/vehicle-rental', {
      bookingId: bookingId || null,
      supplierId,
      vehicle_type: vehicleType || null,
      rental_date: rentalDate || null,
      cost: Number(cost) || 0,
      // `status` lo mette il backend a 'to_pay': il pagamento si registra
      // dal COD8, a fattura ricevuta.
    }).then((r) => r.data);
  },

};
